import React, { createContext, useContext, useState } from "react"
import { GridApiRef, useGridApiRef } from "@mui/x-data-grid-pro"

import { DataState, IDataApi, useDataSubcontext } from "./components/DataSource"
import { FiltersApi, FiltersState, useFiltersSubcontext } from "./components/Filters"
import { SearchApi, SearchState, useSearchSubcontext } from "./components/SearchField"
import { SelectionApi, SelectionState, useSelectionSubcontext } from "./components/Selection"
import { ColumnsState, IColumnsApi, useColumnsSubcontext } from "./components/TableColumns"
import { DrawerState, IDrawerApi, useDrawerSubcontext } from "./components/TableDrawer"
import { ISubContext } from "./context/types"
import { ITableApi, TableState, useTableSubcontext } from "./DataTable/context"

import { getPropsDefaults, LifecycleState, TableProps } from "./types"
import { TableApi } from "./api/types"
import { EditorState, IEditorApi, useEditorSubcontext } from "./components/Editor"

interface ITableContextType {
  lifecycle: {
    state: number;
    increment: () => void;
  };
  muiGridApiRef: GridApiRef;
  props: TableProps;

  table: ISubContext<TableState, ITableApi>;
  columns: ISubContext<ColumnsState, IColumnsApi>,
  selection: ISubContext<SelectionState, SelectionApi>,
  data: ISubContext<DataState, IDataApi>,
  editor: ISubContext<EditorState, IEditorApi>;
  drawer: ISubContext<DrawerState, IDrawerApi>,
  filters: ISubContext<FiltersState, FiltersApi>,
  search: ISubContext<SearchState, SearchApi>,
}

const defaultContextValue = {
  lifecycle: {
    state: 0,
  },
} as ITableContextType

const TableContext = createContext(defaultContextValue)

TableContext.displayName = "DataTable"

function TableContextProvider(props: any) {

  const { children } = props
  const muiGridApiRef = useGridApiRef()
  const [lifecycle, setLifecycle] = useState(0)

  const context: ITableContextType = {
    lifecycle: {
      state: lifecycle,
      increment: () => {
        setLifecycle((value) => value + 1)
      }
    },
    muiGridApiRef: muiGridApiRef,
    props: {
      ...getPropsDefaults(),
      ...props as TableProps
    },
    table: useTableSubcontext(),
    columns: useColumnsSubcontext(),
    selection: useSelectionSubcontext(muiGridApiRef),
    data: useDataSubcontext(),
    editor: useEditorSubcontext(),
    filters: useFiltersSubcontext(),
    search: useSearchSubcontext(),
    drawer: useDrawerSubcontext()
  }

  if (props.apiRef) {
    props.apiRef.current = {
      selection: context.selection.api,
      search: context.search.api,
      data: context.data.api,
      editor: context.editor.api,
      filters: context.filters.api,
    } as TableApi
  }

  return <TableContext.Provider value={context}>{children}</TableContext.Provider>
}

function useTableContext() {
  const context = useContext(TableContext)

  if (context === undefined) {
    throw new Error("[useTableContext]")
  }

  return context
}

export { TableContextProvider, useTableContext }
